import React, { useState, useEffect } from "react";
import { Search, Filter, Loader2, AlertCircle } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useTheme } from "../context/ThemeContext";
import api from "../services/api";
import { formatLagos, formatLagosTime, isWithinTimeFilter } from '../utils/tripLog';

const statusColors = {
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
  active: 'bg-blue-100 text-blue-800',
  expired: 'bg-gray-100 text-gray-800'
};

const FleetManagement = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topPickupZone, setTopPickupZone] = useState('N/A');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  });
  const [allBookings, setAllBookings] = useState([]);
  const [fetchingAll, setFetchingAll] = useState(false);

  // Theme variables
  const bgColor = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = isDark ? 'bg-gray-800' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';
  const hoverBg = isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50';
  const inputBg = isDark ? 'bg-gray-700' : 'bg-white';
  const inputText = isDark ? 'text-white' : 'text-gray-900';
  const headerBg = isDark ? 'bg-gray-800' : 'bg-gray-50';

  // Calculate top pickup zone from bookings
  const calculateTopPickupZone = (bookings) => {
    console.log('Calculating top pickup zone from', bookings?.length, 'bookings');
    try {
      if (!bookings || bookings.length === 0) {
        console.log('No bookings found, returning N/A');
        return 'N/A';
      }
      
      const pickupCounts = {};
      
      // Count occurrences of each pickup location
      bookings.forEach((booking, index) => {
        const pickup = booking.pickup?.address || booking.pickupLocationName || 'Unknown';
        console.log(`Booking ${index + 1} pickup:`, {
          pickupAddress: booking.pickup?.address,
          pickupLocationName: booking.pickupLocationName,
          selected: pickup
        });
        if (pickup && pickup !== 'Unknown') {
          pickupCounts[pickup] = (pickupCounts[pickup] || 0) + 1;
        }
      });
      
      console.log('Pickup counts:', pickupCounts);
      
      // Find the pickup location with the highest count
      let topZone = 'N/A';
      let maxCount = 0;
      
      Object.entries(pickupCounts).forEach(([zone, count]) => {
        if (count > maxCount) {
          console.log(`New top zone: ${zone} (${count} bookings)`);
          maxCount = count;
          topZone = zone;
        }
      });
      
      console.log('Final top pickup zone:', topZone);
      return topZone || 'N/A';
    } catch (error) {
      console.error('Error calculating top pickup zone:', error);
      return 'N/A';
    }
  };

  // Fetch all bookings with pagination
  const fetchAllBookings = async () => {
    try {
      setFetchingAll(true);
      setLoading(true);
      let allFetchedBookings = [];
      let currentPage = 1;
      let totalPages = 1;
      
      do {
        const response = await api.get(`/v1/admin/bookings?page=${currentPage}&limit=20`);
        const { data, pagination: pageInfo } = response.data;
        
        if (data && data.length > 0) {
          allFetchedBookings = [...allFetchedBookings, ...data];
          totalPages = pageInfo?.totalPages || 1;
          currentPage++;
          
          // Update pagination state with current progress
          setPagination(prev => ({
            ...prev,
            page: pageInfo.page,
            total: pageInfo.total,
            totalPages: pageInfo.totalPages,
            hasNext: pageInfo.page < pageInfo.totalPages,
            hasPrev: pageInfo.page > 1
          }));
        } else {
          break;
        }
      } while (currentPage <= totalPages);

      setAllBookings(allFetchedBookings);
      setBookings(allFetchedBookings);
      const topZone = calculateTopPickupZone(allFetchedBookings);
      setTopPickupZone(topZone);
      localStorage.setItem('topPickupZone', topZone);
      setError(null);
    } catch (err) {
      console.error('Error fetching all bookings:', err);
      setError('Failed to load all bookings. Please try again.');
    } finally {
      setLoading(false);
      setFetchingAll(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchAllBookings();
  }, []);

  // Function to refresh the data
  const refreshData = () => {
    fetchAllBookings();
  };

  // Original fetch function (kept for reference)
  const fetchBookings = async (page = 1, limit = 20) => {
    try {
      setLoading(true);
      const response = await api.get(`/v1/admin/bookings?page=${page}&limit=${limit}`);
      const { data, pagination: pageInfo } = response.data;
      
      setBookings(data || []);
      setPagination({
        page: pageInfo.page,
        limit: pageInfo.limit,
        total: pageInfo.total,
        totalPages: pageInfo.totalPages,
        hasNext: pageInfo.page < pageInfo.totalPages,
        hasPrev: pageInfo.page > 1
      });
      
      const topZone = calculateTopPickupZone(data || []);
      setTopPickupZone(topZone);
      localStorage.setItem('topPickupZone', topZone);
      setError(null);
      return data;
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings. Please try again.');
      setBookings([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Initial load with pagination
  useEffect(() => {
    fetchBookings();
  }, []);

  // Filter bookings based on search, status, and time period
  useEffect(() => {
    const filterBookings = () => {
      return bookings.filter(booking => {
        // Search filter
        const matchesSearch = 
          booking.passengerId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          booking._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          booking.pickupLocationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          booking.dropoffLocationName?.toLowerCase().includes(searchTerm.toLowerCase());

        // Status filter
        const matchesStatus = statusFilter === "all" || 
                            booking.status.toLowerCase() === statusFilter.toLowerCase();
        
        // Time period filter, on the Lagos calendar
        const matchesTime = isWithinTimeFilter(booking.createdAt, timeFilter);

        return matchesSearch && matchesStatus && matchesTime;
      });
    };
    
    setFilteredBookings(filterBookings());
  }, [bookings, searchTerm, statusFilter, timeFilter]);

  // Format date
  const formatDate = (dateString) => {
    // Keeps the old "05 Oct, 2026" look.
    const day = formatLagos(dateString, 'en-GB', { day: '2-digit' }, null);
    if (!day) return 'Invalid date';
    const month = formatLagos(dateString, 'en-GB', { month: 'short' });
    const year = formatLagos(dateString, 'en-GB', { year: 'numeric' });
    return `${day} ${month}, ${year}`;
  };

  // Format time
  const formatTime = (dateString) => {
    return formatLagosTime(dateString, 'en-US', { hour: 'numeric', minute: '2-digit' }, '--:--');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          {/* Page Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className={`text-2xl font-semibold ${textColor}`}>Ride Bookings</h1>
              <p className={`text-sm ${textSecondary}`}>View and manage all ride bookings</p>
            </div>

            <div className="flex gap-3">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className={`px-3 py-2 border ${borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 ${inputBg} ${inputText}`}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`px-3 py-2 border ${borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 ${inputBg} ${inputText}`}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Search Bar */}
          <div className={`${cardBg} p-4 mb-6 transition-colors !border-none !shadow-none`}>
            <div className="relative w-full md:w-96">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${textSecondary}`} size={18} />
              <input
                type="text"
                placeholder="Search"
                className={`w-full pl-10 pr-4 py-2 border ${borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${inputBg} ${inputText} transition-colors`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Loading and Error States */}
          {fetchingAll && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md flex items-center">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              <span>Loading all bookings... {Math.min(100, Math.round((pagination.page / pagination.totalPages) * 100))}%</span>
            </div>
          )}
          {loading && !fetchingAll ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span>Loading bookings...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className={`${cardBg} overflow-hidden transition-colors rounded-lg shadow`}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Passenger
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pickup
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dropoff
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fare
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBookings.length > 0 ? (
                      filteredBookings.map((booking) => (
                        <tr key={booking._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {booking.passengerId?.name || 'N/A'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {booking.passengerId?.phone || ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{booking.pickupLocationName || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{booking.dropoffLocationName || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(booking.createdAt)}</div>
                            <div className="text-sm text-gray-500">{formatTime(booking.createdAt)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(booking.fare || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              statusColors[booking.status] || 'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.status || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                          No bookings found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination can be added here when needed */}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default FleetManagement;